'use client';

/**
 * The welcome a trip gets when it lands from a shared page ("Take this
 * trip"): a slim banner at the top of the plan that stays until you save,
 * edit or dismiss it. It says what happened (a copy, as a draft), what the
 * Save button adds (it goes into your trips), and offers Undo, which puts
 * back whatever the take replaced and returns to the shared page.
 */
import React, { useState } from 'react';
import { IconCheck, IconX } from './icons';

export function takenNoticeCopy(_name?: string): { title: string; detail: string } {
  return {
    title: 'Copied to your planner as a draft',
    detail: 'Save trip adds it to your trips.',
  };
}

export default function TakenNotice({
  name,
  onUndo,
  onDismiss,
}: {
  name: string;
  /** Put back the trip this replaced and go back to the shared page. */
  onUndo?: () => void;
  onDismiss?: () => void;
}) {
  const [gone, setGone] = useState(false);
  const { title, detail } = takenNoticeCopy(name);
  if (gone) return null;
  return (
    <div className="px-4 pb-3">
      <div
        role="status"
        aria-live="polite"
        data-testid="trip-status"
        className="flex animate-tp-enter items-start gap-3 rounded-tp-lg bg-tp-ok-soft py-2.5 pl-3 pr-1.5 ring-1 ring-inset ring-tp-ok/20 motion-reduce:animate-none"
      >
        <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-tp-ok text-white" aria-hidden="true">
          <IconCheck className="h-3.5 w-3.5" />
        </span>
        <p className="m-0 min-w-0 flex-1 text-tp-sm leading-5 text-tp-ink">
          <span className="block font-semibold">{title}</span>
          <span className="block text-tp-muted">{detail}</span>
        </p>
        {onUndo && (
          <button
            type="button"
            onClick={onUndo}
            className="h-8 min-h-0 flex-none rounded-tp border-0 bg-transparent px-2.5 text-tp-sm font-semibold text-tp-ink underline decoration-tp-ink/30 underline-offset-2 transition-colors duration-150 ease-tp-out hover:bg-white/60 hover:decoration-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
            data-testid="taken-undo"
          >
            Undo
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setGone(true);
            onDismiss?.();
          }}
          aria-label="Dismiss"
          className="flex h-8 w-8 min-h-0 min-w-0 flex-none items-center justify-center rounded-full border-0 bg-transparent p-0 text-tp-muted transition-colors duration-150 ease-tp-out hover:bg-white/60 hover:text-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
        >
          <IconX className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
